import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Search from './components/search';
import Login from './components/login';
import FindADealer from './components/findADealer';
import MenuOverlay from './components/menuOverlay';
import NavigationItem from './components/navigationItem';
import { HeaderModel } from './models/header.model';
import './styles.scss';
import { MenuContext, useMenuReducer } from './components/menuContext';
import Logo from './components/logo';
import MobileMenu from './components/mobileMenu';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import useMediaQuery from '../../hooks/useMediaQuery';
import useStickyWrapper from '../../hooks/useStickyWrapper';
import CountrySelector from './components/countrySelector';
import { documentService, windowService } from '../../utils/window.utils';
import breakpoints from '../../constants/breakpoints.constants';
import { buildAndPriceClass } from '../../constants/buildAndPrice.constants';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import useSitecoreContext from '../../hooks/useSitecoreContext';
import HighLevelAlertMessage from './components/highLevelAlertMessage';

const headerHeight = 85;
const headerHeightStr = `${headerHeight}px`;

const Header = (props) => {
  const isEEActive = isEditorActive();
  const context = useSitecoreContext();
  const isHighAlertMessage = context?.sitecoreContext?.severitySettings?.showSeverityMessage;

  const [navigationVisibility, setNavigationVisibility] = useState(false);
  useEffect(() => {
    // set height as css property so that content can be shifted below
    if (!isEEActive) {
      documentService().body.style.setProperty('--navigation--Height--actual', headerHeightStr);
    }
    setNavigationVisibility(true);
    const loadingDiv = documentService().getElementById('loading');
    if (loadingDiv) {
      loadingDiv.remove();
      const rootElement = documentService().getElementById('loading');
      if (rootElement) rootElement.style.display = 'none';
    }
    return (): void => {
      documentService().body.style.removeProperty('--navigation--Height--actual');
    };
  }, [isEEActive]);

  const breakpointXL = useMediaQuery(`(min-width: ${breakpoints.dsk}px)`, false);
  const breakpointLg = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const model = new HeaderModel(props);  

  const fields = model.fields;

  const [menuState, menuDispatcher] = useMenuReducer();
  const isMenuOpen = menuState.some(({ isOpen }) => isOpen);

  const [isHiddenUp, setHiddenUp] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isBodyScrollBlocked, _setBodyScrollBlocked] = useState<boolean | null>(null);

  const handleMenuOpen = (quickLinks) => {
    const scrollBarWidth = windowService().innerWidth - documentService().body.clientWidth;
    const scrollBarWidthStr = `${scrollBarWidth}px`;
    documentService().body.style.paddingRight = scrollBarWidthStr;
    if (wrapperRef.current) {
      wrapperRef.current.style.paddingRight = scrollBarWidthStr;
    }
    quickLinks.forEach((element) => {
      const actualElement = element as unknown as HTMLElement;
      if (breakpointXL) {
        actualElement.style.right = scrollBarWidthStr;
        actualElement.style.overflow = 'hidden';
      } else {
        actualElement.style.removeProperty('right');
        actualElement.style.removeProperty('overflow');
      }
    });
  };

  const handleMenuClosed = (quickLinks) => {
    documentService().documentElement.style.removeProperty('--removed-scrollbar-width');
    documentService().body.style.removeProperty('padding-right');
    if (wrapperRef.current) {
      wrapperRef.current.style.removeProperty('padding-right');
    }
    quickLinks.forEach((element) => {
      const actualElement = element as unknown as HTMLElement;
      actualElement.style.removeProperty('right');
      actualElement.style.removeProperty('overflow');
    });
  };

  useEffect(() => {
    if (isEEActive) return;

    // logic to block the scroll when the menu is open, without breaking anything
    _setBodyScrollBlocked((prevIsBodyScrollBlocked) => {
      if (prevIsBodyScrollBlocked != null) {
        const quickLinks = documentService()?.querySelectorAll?.('.quick-links');
        if (!prevIsBodyScrollBlocked && isMenuOpen) {
          handleMenuOpen(quickLinks);
        } else if (prevIsBodyScrollBlocked && !isMenuOpen) {
          handleMenuClosed(quickLinks);
        }
        documentService().body.classList.toggle('no-scroll', isMenuOpen);
      }
      return isMenuOpen;
    });
  }, [isEEActive, isMenuOpen, breakpointXL]);

  useEffect(() => {
    if (!isEEActive && !isBodyScrollBlocked) {
      let lastScrollPosition: number | null;
      const headerScrollCallback = (): void => {
        const newScrollPosition = windowService().pageYOffset;
        // do not change if the scroll position has not changed or if a menu is open
        if (lastScrollPosition != null && newScrollPosition !== lastScrollPosition) {
          const nextElementPosition = (documentService()?.querySelector?.('.carousel-wrapper+*') as HTMLElement)?.offsetTop;
          const isAfterMagnetic = newScrollPosition > (nextElementPosition ?? windowService().outerHeight - headerHeight);
          const shouldBeHiddenUp = newScrollPosition > lastScrollPosition && isAfterMagnetic;
          const isBuildAndPricePresent = documentService()?.querySelector?.(`.${buildAndPriceClass}`);
          if (shouldBeHiddenUp) {
            menuDispatcher({ type: 'closeAll' });
          }
          setHiddenUp(shouldBeHiddenUp && !isBuildAndPricePresent);
        }
        lastScrollPosition = newScrollPosition;
      };

      windowService().addEventListener('scroll', headerScrollCallback);
      return (): void => {
        windowService().removeEventListener('scroll', headerScrollCallback);
      };
    }
  }, [isEEActive, isBodyScrollBlocked]); // eslint-disable-line

  const baseClassName = 'navigation';
  const wrapperClassName =
    baseClassName + (isEEActive ? ' ' + baseClassName + '--EE-active' : '') + (isHiddenUp ? ' sticky-element--hide' : '');

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
     <MenuContext.Provider value={[menuState, menuDispatcher]}>
      <div className={wrapperClassName} hidden={!navigationVisibility} ref={wrapperRef}>
        <MenuOverlay />
        <nav className={`navigation__container ${isHiddenUp ? 'navigation__container--hidden' : ''}`}>
          <Logo fields={fields} />
          {breakpointXL && (
            <ul className="navigation__item-wrapper navigation__item-wrapper--main">
              {model.navigationItemModelsArr?.map((navigationItemModel) => (
                <NavigationItem key={navigationItemModel?.id} model={navigationItemModel} />
              ))}
            </ul>
          )}
          <ul className="navigation__item-wrapper navigation__item-wrapper--right">
            {breakpointLg && (
              <>
                {fields?.findDealer && <FindADealer fields={fields} />}
                {fields?.country && <CountrySelector model={model.countrySelector} />}
                {fields?.search && <Search fields={fields} sitecoreContext={props?.sitecoreContext} />}
                {fields?.login && <Login fields={fields} />}
              </>
            )}
            {!breakpointXL && <MobileMenu model={model} fields={fields} breakpointLg={breakpointLg} />}
          </ul>
        </nav>
      </div>
      {isHighAlertMessage && <HighLevelAlertMessage />}
    </MenuContext.Provider>
  );
};

const HeaderWrapper = (props) => {
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const isPreview = props?.sitecoreContext?.pageState === 'preview';
  const isEE = isEEActive || isPreview;

  const stickyWrapperEle = useStickyWrapper();

  useEffect(() => {
    if (isEE) {
      documentService()?.getElementById?.('root')?.classList?.add('root-padding-none');
    }
  }, [isEE]);

  if (isEE) return <Header {...props} />;

  if (!stickyWrapperEle) return null;

  return createPortal(<Header {...props} />, stickyWrapperEle);
};

export default withSitecoreContext()(HeaderWrapper);
